import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTES } from 'shared/constants/ROUTES';
import ListContainer from './List/ListContainer';
import ProgramSetupContainer from './ProgramSetup/ProgramSetupContainer';
import { Drawer, DrawerScrim, DrawerWrap, PsGlobalStyle } from './ProgramSetup/ProgramSetup.styled';

// How long the drawer's slide-out CSS transition takes — must match the
// `transition` duration on the `Drawer` styled component.
const DRAWER_TRANSITION_MS = 300;

// A single route matches LIST/CREATE/EDIT (see Routes.tsx) so this component
// never unmounts moving between them: the list stays mounted underneath and
// the drawer just animates open/closed based on the current path.
export const ProgramSetupConsole = () => {
  const { push } = useHistory();
  const location = useLocation();
  const isCreate = location.pathname === ROUTES.RADIO.PROGRAMSETUP.CREATE;
  const isEdit = location.pathname === ROUTES.RADIO.PROGRAMSETUP.EDIT;
  const routeOpen = isCreate || isEdit;

  const [mounted, setMounted] = useState(routeOpen);
  const [entered, setEntered] = useState(false);
  const editIdRef = useRef<string | undefined>(undefined);
  const defaultStationIdRef = useRef<string | undefined>(undefined);
  const defaultStationNameRef = useRef<string | undefined>(undefined);

  if (routeOpen) {
    editIdRef.current = (location.state as { id?: string } | undefined)?.id;
    const createState = isCreate
      ? (location.state as { defaultStationId?: string; defaultStationName?: string } | undefined)
      : undefined;
    defaultStationIdRef.current = createState?.defaultStationId;
    defaultStationNameRef.current = createState?.defaultStationName;
  }

  useEffect(() => {
    if (routeOpen) {
      setMounted(true);
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    setEntered(false);
    const timeout = setTimeout(() => setMounted(false), DRAWER_TRANSITION_MS);
    return () => clearTimeout(timeout);
  }, [routeOpen]);

  const close = useCallback(() => push(ROUTES.RADIO.PROGRAMSETUP.LIST), [push]);

  useEffect(() => {
    if (!routeOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [routeOpen, close]);

  return (
    <div className="ps-redesign">
      <PsGlobalStyle />
      <ListContainer />
      {mounted && (
        <DrawerWrap>
          <DrawerScrim $open={entered} onClick={close} />
          <Drawer $open={entered} role="dialog" aria-modal="true">
            <ProgramSetupContainer
              editId={editIdRef.current}
              defaultStationId={defaultStationIdRef.current}
              defaultStationName={defaultStationNameRef.current}
              onClose={close}
            />
          </Drawer>
        </DrawerWrap>
      )}
    </div>
  );
};

export default ProgramSetupConsole;
